import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdAssignment } from "react-icons/md";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import CourseService from "../../services/api/CourseService";
import { NotifyContext } from "../../context/notifyContext";
import {
  Exercise,
  ExerciseAnalytics,
  QuestionAnalytics,
} from "../../Types/Courses";
import Button from "../../UI/Button/Button";

const initialStats: ExerciseAnalytics = {
  questions_count: 0,
  used_in_lessons: 0,
  used_in_assignments: 0,
  attempts: 0,
  correct_answers: 0,
  accuracy_rate: 0,
};

export default function ExerciseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  const [loading, setLoading] = useState(true);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [stats, setStats] = useState<ExerciseAnalytics>(initialStats);
  const [questionsStats, setQuestionsStats] = useState<QuestionAnalytics[]>([]);

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    {
      label: "Exercices",
      icon: <MdAssignment />,
      is_link: true,
      href: "/app/courses/exercises",
    },
    { label: "Détails", is_link: false },
  ]);

  const cards = useMemo(
    () => [
      { label: "Questions", value: stats.questions_count },
      { label: "Utilisé dans des leçons", value: stats.used_in_lessons },
      { label: "Utilisé dans des devoirs", value: stats.used_in_assignments },
      { label: "Tentatives", value: stats.attempts },
      { label: "Bonnes réponses", value: stats.correct_answers },
      { label: "Précision globale", value: `${stats.accuracy_rate}%` },
    ],
    [stats]
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    CourseService.getExerciseAnalytics(Number(id))
      .then((response: any) => {
        setExercise(response?.data?.exercise || null);
        setStats({ ...initialStats, ...(response?.data?.stats || {}) });
        setQuestionsStats(response?.data?.questions_stats || []);
      })
      .catch((error: any) => {
        notify(
          error?.message || error || "Impossible de charger les détails de l'exercice",
          "error"
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase text-gray-500">Exercice</p>
          <h2 className="text-xl font-semibold">{exercise?.title || "Détails de l'exercice"}</h2>
          <p className="text-sm text-gray-600">{exercise?.description || "Aucune description."}</p>
        </div>
        <div className="flex gap-2">
          <Button color="info" onClick={() => navigate(`/app/courses/exercises/${id}/edit`)}>
            Modifier
          </Button>
          <Button onClick={() => navigate("/app/courses/exercises")}>Retour liste</Button>
        </div>
      </div>

      {loading ? <div className="bg-white rounded shadow p-6">Chargement...</div> : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded shadow p-4">
            <p className="text-xs text-gray-500">{card.label}</p>
            <p className="text-xl font-semibold mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Performance par question</h3>
        {questionsStats.length === 0 ? (
          <p className="text-sm text-gray-500">
            Aucune statistique disponible pour le moment.
          </p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left px-3 py-2">Ordre</th>
                  <th className="text-left px-3 py-2">Question</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Points</th>
                  <th className="text-left px-3 py-2">Tentatives</th>
                  <th className="text-left px-3 py-2">Bonnes réponses</th>
                  <th className="text-left px-3 py-2">Précision</th>
                </tr>
              </thead>
              <tbody>
                {questionsStats.map((item) => (
                  <tr key={item.id} className="border-b">
                    <td className="px-3 py-2">{item.order}</td>
                    <td className="px-3 py-2">{item.question_text || "-"}</td>
                    <td className="px-3 py-2">{item.type}</td>
                    <td className="px-3 py-2">{item.points}</td>
                    <td className="px-3 py-2">{item.attempts}</td>
                    <td className="px-3 py-2">{item.correct_answers}</td>
                    <td className="px-3 py-2">{item.accuracy_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
